import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Container, Card, Table, Form, Button, Badge, Spinner, Modal, Alert } from 'react-bootstrap';
import axios from 'axios';
import { showToast as toast } from '../toastManager';

const MODULES = [
  { id: 'context_images',    label: 'Context & Images',  short: 'Context' },
  { id: 'youtube',           label: 'YouTube Data',      short: 'YouTube' },
  { id: 'section_json',      label: 'Section JSON',      short: 'Sec. JSON', lpOnly: true },
  { id: 'ai_campaign',       label: 'AI Campaign',       short: 'AI Camp.',  lpOnly: true },
];

const YOUTUBE_PERIOD_OPTIONS = [
  { value: 1,  label: '1 month'            },
  { value: 3,  label: '3 months'           },
  { value: 6,  label: '6 months (default)' },
  { value: 12, label: '12 months'          },
];

const STEP_ICONS  = { pending: '○', running: '⏳', completed: '✓', failed: '✗' };
const STEP_COLORS = { pending: '#888', running: '#0d6efd', completed: '#198754', failed: '#dc3545' };

// fetch-page-elements polling tuning. The endpoint now returns 202 + jobId for
// cold scrapes (Firecrawl can take >100 s — beyond Cloudflare's HTTP limit).
const ELEMENTS_POLL_INTERVAL_MS = 1500;
const ELEMENTS_MAX_POLLS        = 200;   // ~5 minutes at 1.5 s
const ELEMENTS_MAX_FAILURES     = 3;     // consecutive transient errors tolerated

// selections: Map<pageUrl, { modules: Set<string>, youtubePeriod: number }>
const mkSel = () => ({ modules: new Set(), youtubePeriod: 6 });

const ReRunModules = ({ shop_domain, app_url, excludeModules = [] }) => {
  const ACTIVE_MODULES = useMemo(
    () => MODULES.filter(m => !excludeModules.includes(m.id)),
    [excludeModules]
  );

  const [websitePages,     setWebsitePages]     = useState([]);
  const [landingPages,     setLandingPages]     = useState([]);
  const [selections,       setSelections]       = useState(new Map());
  const [running,          setRunning]          = useState(false);
  const [jobSteps,         setJobSteps]         = useState([]);
  const [lastRun,          setLastRun]          = useState(null);
  const [conflicts,        setConflicts]        = useState([]);
  const [showConflict,     setShowConflict]     = useState(false);
  const [checkLoading,     setCheckLoading]     = useState(false);
  const [deleting,         setDeleting]         = useState(null);
  const [showElementSelect, setShowElementSelect] = useState(false);
  const [pageElements,     setPageElements]     = useState({});
  const [elementSelections, setElementSelections] = useState({});
  const [elementsLoading,  setElementsLoading]  = useState(false);
  const [copiedStepId,     setCopiedStepId]     = useState(null);
  const pollRef             = useRef(null);
  const pendingSelectionsRef = useRef([]);
  // Track every active fetch-page-elements polling interval so we can clear
  // them all on unmount. Sequential loadPageElements means at most one at a
  // time today, but a Set keeps us safe if that ever changes.
  const elementsPollIntervalsRef = useRef(new Set());

  useEffect(() => {
    if (!shop_domain || !app_url) return;
    loadSelectedPages();
    loadStatus();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      for (const id of elementsPollIntervalsRef.current) clearInterval(id);
      elementsPollIntervalsRef.current.clear();
    };
  }, [shop_domain, app_url]);

  const loadSelectedPages = async () => {
    try {
      const res = await axios.get(`${app_url}/api/settings/domains-and-urls/selected-pages`, { withCredentials: true });
      if (res.data.success) {
        const wp = res.data.website_pages  || [];
        const lp = res.data.landing_pages  || [];
        setWebsitePages(wp);
        setLandingPages(lp);
        // Initialise selections map — all pages, no modules pre-selected
        const map = new Map();
        [...wp, ...lp].forEach(u => map.set(u, mkSel()));
        setSelections(map);
      }
    } catch { /* non-fatal */ }
  };

  const loadStatus = async () => {
    try {
      const res = await axios.get(
        `${app_url}/api/dev/re-run-modules/status?shop=${encodeURIComponent(shop_domain)}`,
        { withCredentials: true }
      );
      if (!res.data.success) return;
      if (res.data.active_job?.jobId) {
        const { jobId, steps } = res.data.active_job;
        if (steps) setJobSteps(steps);
        setRunning(true);
        startPolling(jobId);
        return;
      }
      if (res.data.last_run?.steps) {
        setJobSteps(res.data.last_run.steps);
        setLastRun(res.data.last_run);
      }
    } catch { /* non-fatal */ }
  };

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const poll = await axios.get(`${app_url}/api/firebase/job_status/${jobId}`, { withCredentials: true });
        const job  = poll.data?.job;
        if (!job) return;
        if (job.steps) setJobSteps(job.steps);
        if (job.status === 'succeeded') {
          clearInterval(pollRef.current);
          setRunning(false);
          toast.success('Re-run completed successfully');
        } else if (job.status === 'failed') {
          clearInterval(pollRef.current);
          setRunning(false);
          toast.error('One or more modules failed. See status below.');
        }
      } catch {
        clearInterval(pollRef.current);
        setRunning(false);
        toast.error('Lost connection while polling status');
      }
    }, 1500);
  };

  // ── Selection helpers ────────────────────────────────────────────────────────

  const toggleModule = (page, modId) => {
    setSelections(prev => {
      const next = new Map(prev);
      const sel  = { ...(next.get(page) || mkSel()), modules: new Set(next.get(page)?.modules) };
      sel.modules.has(modId) ? sel.modules.delete(modId) : sel.modules.add(modId);
      next.set(page, sel);
      return next;
    });
  };

  const setYoutubePeriod = (page, val) => {
    setSelections(prev => {
      const next = new Map(prev);
      const sel  = { ...(next.get(page) || mkSel()), modules: new Set(next.get(page)?.modules) };
      sel.youtubePeriod = Number(val);
      next.set(page, sel);
      return next;
    });
  };

  const toggleColumnAll = (modId, selectAll) => {
    setSelections(prev => {
      const next = new Map(prev);
      for (const [page, sel] of next) {
        const isLP = landingPages.includes(page);
        if (MODULES.find(m => m.id === modId)?.lpOnly && !isLP) continue;
        const newSel = { ...sel, modules: new Set(sel.modules) };
        selectAll ? newSel.modules.add(modId) : newSel.modules.delete(modId);
        next.set(page, newSel);
      }
      return next;
    });
  };

  const toggleRowAll = (page, selectAll) => {
    const isLP = landingPages.includes(page);
    setSelections(prev => {
      const next = new Map(prev);
      const sel  = { ...(next.get(page) || mkSel()) };
      sel.modules = new Set(selectAll ? ACTIVE_MODULES.filter(m => !m.lpOnly || isLP).map(m => m.id) : []);
      next.set(page, sel);
      return next;
    });
  };

  // ── Build payload ────────────────────────────────────────────────────────────

  const buildSelectionsArr = () => {
    const arr = [];
    for (const [page, sel] of selections) {
      if (sel.modules.size === 0) continue;
      arr.push({
        page,
        is_landing_page:      landingPages.includes(page),
        modules:              [...sel.modules],
        youtube_period_months: sel.youtubePeriod,
      });
    }
    return arr;
  };

  // ── Run flow ─────────────────────────────────────────────────────────────────

  const handleRun = async () => {
    const selectionsArr = buildSelectionsArr();
    if (selectionsArr.length === 0) return;

    setCheckLoading(true);
    try {
      const res = await axios.post(
        `${app_url}/api/dev/re-run-modules/pre-check`,
        { shop: shop_domain, selections: selectionsArr },
        { withCredentials: true }
      );
      if (!res.data.success) {
        toast.error(res.data.message || 'Pre-check failed');
        return;
      }
      if (res.data.conflicts?.length > 0) {
        setConflicts(res.data.conflicts);
        setShowConflict(true);
        return;
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Pre-check error');
      return;
    } finally {
      setCheckLoading(false);
    }

    await fetchAndShowElements(selectionsArr);
  };

  const startRun = async (selectionsArr) => {
    setRunning(true);
    setShowConflict(false);
    // Optimistic step list
    const pending = selectionsArr.flatMap(sel =>
      sel.modules.map(mod => ({
        id:     `${sel.page}__${mod}`,
        page:   sel.page,
        module: mod,
        label:  `${MODULES.find(m => m.id === mod)?.label} — ${sel.page}`,
        status: 'pending',
      }))
    );
    setJobSteps(pending);

    try {
      const res = await axios.post(
        `${app_url}/api/dev/re-run-modules`,
        { shop: shop_domain, selections: selectionsArr },
        { withCredentials: true }
      );
      if (res.status !== 202 || !res.data?.jobId) {
        toast.error(res.data?.message || 'Failed to start re-run');
        setRunning(false);
        return;
      }
      startPolling(res.data.jobId);
    } catch (err) {
      setRunning(false);
      toast.error(err?.response?.data?.message || 'Error starting re-run');
    }
  };

  // ── AI Campaign element selection helpers ────────────────────────────────────

  // Promise wrapper around the elements-job polling endpoint. Resolves with
  // job.result on success; rejects on failure, missing job, transient error
  // run, or timeout. Tracks its interval in elementsPollIntervalsRef so the
  // unmount cleanup can kill it.
  const awaitJobResult = (jobId) => new Promise((resolve, reject) => {
    let pollCount = 0;
    let failures  = 0;
    const interval = setInterval(async () => {
      pollCount += 1;
      if (pollCount > ELEMENTS_MAX_POLLS) {
        clearInterval(interval);
        elementsPollIntervalsRef.current.delete(interval);
        reject(new Error('Timed out waiting for elements'));
        return;
      }
      try {
        const poll = await axios.get(`${app_url}/api/firebase/job_status/${jobId}`, { withCredentials: true });
        const job  = poll.data?.job;
        if (!job) {
          clearInterval(interval);
          elementsPollIntervalsRef.current.delete(interval);
          reject(new Error('Job lost'));
          return;
        }
        failures = 0;
        if (job.status === 'succeeded') {
          clearInterval(interval);
          elementsPollIntervalsRef.current.delete(interval);
          resolve(job.result || {});
        } else if (job.status === 'failed') {
          clearInterval(interval);
          elementsPollIntervalsRef.current.delete(interval);
          reject(new Error(job.error?.message || 'Job failed'));
        }
      } catch {
        failures += 1;
        if (failures >= ELEMENTS_MAX_FAILURES) {
          clearInterval(interval);
          elementsPollIntervalsRef.current.delete(interval);
          reject(new Error('Lost connection'));
        }
      }
    }, ELEMENTS_POLL_INTERVAL_MS);
    elementsPollIntervalsRef.current.add(interval);
  });

  // Fetches elements for a single page. Handles both the 200 (cache hit)
  // and 202 (queued job) responses transparently for the caller.
  const fetchOnePageElements = async (page, forceRefresh) => {
    const res = await axios.post(
      `${app_url}/api/dev/re-run-modules/fetch-page-elements`,
      { url: page, shop: shop_domain, force_refresh: forceRefresh },
      { withCredentials: true }
    );

    if (res.status === 202 && res.data?.jobId) {
      const result = await awaitJobResult(res.data.jobId);
      return {
        text_elements: result.text_elements || [],
        images:        result.images        || [],
        from_cache:    result.from_cache    || false,
        fetched_at:    result.fetched_at    || null,
      };
    }
    if (res.data?.success) {
      return {
        text_elements: res.data.text_elements || [],
        images:        res.data.images        || [],
        from_cache:    res.data.from_cache    || false,
        fetched_at:    res.data.fetched_at    || null,
      };
    }
    throw new Error(res.data?.message || 'Unexpected response from fetch-page-elements');
  };

  const loadPageElements = async (pages, forceRefresh = false) => {
    const fetched = {};
    for (const page of pages) {
      try {
        fetched[page] = await fetchOnePageElements(page, forceRefresh);
      } catch { /* skip failed pages — matches the prior per-page resilience */ }
    }
    return fetched;
  };

  const applyFetchedElements = (fetched) => {
    const initSel = {};
    for (const [url, data] of Object.entries(fetched)) {
      initSel[url] = {
        text_elements: [...data.text_elements],
        images:        [...data.images],
      };
    }
    setPageElements(fetched);
    setElementSelections(initSel);
  };

  const fetchAndShowElements = async (selectionsArr) => {
    const aiCampaignPages = selectionsArr
      .filter(s => s.is_landing_page && s.modules.includes('ai_campaign'))
      .map(s => s.page);

    if (aiCampaignPages.length === 0) {
      await startRun(selectionsArr);
      return;
    }

    pendingSelectionsRef.current = selectionsArr;
    setPageElements({});
    setElementSelections({});
    setElementsLoading(true);
    setShowElementSelect(true);

    const fetched = await loadPageElements(aiCampaignPages, false);
    applyFetchedElements(fetched);
    setElementsLoading(false);
  };

  const handleRefetchElements = async () => {
    const pages = pendingSelectionsRef.current
      .filter(s => s.is_landing_page && s.modules.includes('ai_campaign'))
      .map(s => s.page);
    setElementsLoading(true);
    const fetched = await loadPageElements(pages, true);
    applyFetchedElements(fetched);
    setElementsLoading(false);
  };

  const toggleElementSelection = (url, type, item) => {
    setElementSelections(prev => {
      const current = prev[url] || { text_elements: [], images: [] };
      const list    = current[type] || [];
      const key     = type === 'text_elements' ? 'query_selector' : 'query_selector';
      const exists  = list.some(el => el[key] === item[key]);
      return {
        ...prev,
        [url]: {
          ...current,
          [type]: exists
            ? list.filter(el => el[key] !== item[key])
            : [...list, item],
        },
      };
    });
  };

  const handleConfirmElements = async () => {
    const enriched = pendingSelectionsRef.current.map(sel => {
      if (!sel.modules.includes('ai_campaign') || !elementSelections[sel.page]) return sel;
      return { ...sel, ai_campaign_elements: elementSelections[sel.page] };
    });
    setShowElementSelect(false);
    await startRun(enriched);
  };

  const anyElementSelected = Object.values(elementSelections).some(
    d => (d.text_elements?.length || 0) + (d.images?.length || 0) > 0
  );

  const handleDeleteCampaign = async (campaignId) => {
    setDeleting(campaignId);
    try {
      await axios.post(`${app_url}/api/cux/delete-campaign`, { campaign_id: campaignId }, { withCredentials: true });
      // Re-run pre-check to refresh conflicts
      const selectionsArr = buildSelectionsArr();
      const res = await axios.post(
        `${app_url}/api/dev/re-run-modules/pre-check`,
        { shop: shop_domain, selections: selectionsArr },
        { withCredentials: true }
      );
      if (res.data.success) {
        setConflicts(res.data.conflicts || []);
        if ((res.data.conflicts || []).length === 0) setShowConflict(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error deleting campaign');
    } finally {
      setDeleting(null);
    }
  };

  // ── Derived state ─────────────────────────────────────────────────────────────

  const allPages = [...websitePages, ...landingPages];
  const hasAnySelection = [...selections.values()].some(s => s.modules.size > 0);
  const canRun = hasAnySelection && !running;

  const colAllChecked = (modId) => {
    const eligible = allPages.filter(p => !MODULES.find(m => m.id === modId)?.lpOnly || landingPages.includes(p));
    return eligible.length > 0 && eligible.every(p => selections.get(p)?.modules.has(modId));
  };

  const rowAllChecked = (page) => {
    const isLP = landingPages.includes(page);
    const applicable = ACTIVE_MODULES.filter(m => !m.lpOnly || isLP);
    return applicable.length > 0 && applicable.every(m => selections.get(page)?.modules.has(m.id));
  };

  // Group job steps by page for display
  const stepsByPage = jobSteps.reduce((acc, step) => {
    const p = step.page || step.id;
    if (!acc[p]) acc[p] = [];
    acc[p].push(step);
    return acc;
  }, {});

  return (
    <Container fluid className="p-0">
      <div className="main-title mt-4">
        <h2>Re-Run Modules</h2>
      </div>

      <div className="main-accordion-body mt-4 p-2">

        {/* ── Card: Select Pages and Modules ── */}
        <Card className="mb-3 asset-form-card">
          <Card.Header className="py-3" style={{ background: 'white', borderBottom: '1px solid #EAECF0' }}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="sxi-accordion-header-with-icon">
                <div className="sxi-accordion-header-icon" aria-hidden="true">
                  <svg width="58" height="58" viewBox="0 0 58 58" fill="none">
                    <rect x="3" y="3" width="48" height="48" rx="6" fill="#E5EEFF" stroke="#CEDEFF"/>
                    <rect x="15" y="15" width="26" height="26" rx="2" stroke="#2056C1" strokeWidth="1.5" fill="none"/>
                    <line x1="15" y1="22" x2="41" y2="22" stroke="#2056C1" strokeWidth="1.2"/>
                    <line x1="15" y1="29" x2="41" y2="29" stroke="#2056C1" strokeWidth="1.2"/>
                    <line x1="24" y1="15" x2="24" y2="41" stroke="#2056C1" strokeWidth="1.2"/>
                    <line x1="33" y1="15" x2="33" y2="41" stroke="#2056C1" strokeWidth="1.2"/>
                  </svg>
                </div>
                <span>Select Pages and Modules</span>
              </div>
              <Badge bg="secondary" style={{ fontSize: 12 }}>
                {[...selections.values()].reduce((n, s) => n + s.modules.size, 0)} module selections
              </Badge>
            </div>
          </Card.Header>
          <Card.Body className="p-2">
            {allPages.length === 0 ? (
              <p className="text-muted small mb-0 p-2">No pages saved yet. Add pages in Domains &amp; URLs first.</p>
            ) : (
              <Table bordered hover size="sm" responsive className="mb-0" style={{ fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F9FAFB' }}>
                    <th style={{ minWidth: 220, fontWeight: 600, verticalAlign: 'middle' }}>Page</th>
                    <th style={{ width: 40, textAlign: 'center', verticalAlign: 'middle' }}>All</th>
                    {ACTIVE_MODULES.map(mod => (
                      <th key={mod.id} style={{ textAlign: 'center', verticalAlign: 'middle', minWidth: 80 }}>
                        <div style={{ fontWeight: 600 }}>{mod.short}</div>
                        <Form.Check
                          type="checkbox"
                          className="d-inline-block mt-1"
                          checked={colAllChecked(mod.id)}
                          onChange={e => toggleColumnAll(mod.id, e.target.checked)}
                          title={`Select all — ${mod.label}`}
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allPages.map(page => {
                    const isLP  = landingPages.includes(page);
                    const sel   = selections.get(page) || mkSel();
                    const label = isLP ? (
                      <span>{page} <Badge bg="info" style={{ fontSize: 10 }}>LP</Badge></span>
                    ) : page;
                    return (
                      <React.Fragment key={page}>
                        <tr>
                          <td style={{ wordBreak: 'break-all', verticalAlign: 'middle', maxWidth: 280 }}>
                            <small>{label}</small>
                          </td>
                          <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                            <Form.Check
                              type="checkbox"
                              checked={rowAllChecked(page)}
                              onChange={e => toggleRowAll(page, e.target.checked)}
                              title="Select all modules for this page"
                            />
                          </td>
                          {ACTIVE_MODULES.map(mod => {
                            const disabled = mod.lpOnly && !isLP;
                            return (
                              <td key={mod.id} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                {disabled ? (
                                  <span title="Only for landing pages" style={{ color: '#ccc', fontSize: 16 }}>—</span>
                                ) : (
                                  <Form.Check
                                    type="checkbox"
                                    checked={sel.modules.has(mod.id)}
                                    onChange={() => toggleModule(page, mod.id)}
                                  />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                        {/* YouTube period sub-row */}
                        {sel.modules.has('youtube') && (
                          <tr style={{ background: '#FAFBFF' }}>
                            <td colSpan={2} style={{ paddingLeft: 24, borderTop: 0 }}>
                              <small className="text-muted">YouTube fetch period:</small>
                            </td>
                            <td colSpan={3} style={{ borderTop: 0 }}>
                              <Form.Select
                                size="sm"
                                style={{ maxWidth: 160, display: 'inline-block' }}
                                value={sel.youtubePeriod}
                                onChange={e => setYoutubePeriod(page, e.target.value)}
                              >
                                {YOUTUBE_PERIOD_OPTIONS.map(o => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </Form.Select>
                            </td>
                            <td colSpan={2} style={{ borderTop: 0 }} />
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            )}
            <p className="text-muted small mt-2 mb-0 px-2" style={{ fontSize: 11 }}>
              Section JSON (Sec. JSON) applies to landing pages only. "LP" badge = landing page.
            </p>
          </Card.Body>
        </Card>

        {/* ── Card: Run & Status ── */}
        <Card className="mb-4 asset-form-card">
          <Card.Header className="py-3" style={{ background: 'white', borderBottom: '1px solid #EAECF0' }}>
            <div className="sxi-accordion-header-with-icon">
              <div className="sxi-accordion-header-icon" aria-hidden="true">
                <svg width="58" height="58" viewBox="0 0 58 58" fill="none">
                  <rect x="3" y="3" width="48" height="48" rx="6" fill="#E5EEFF" stroke="#CEDEFF"/>
                  <polygon points="21,17 39,27 21,37" fill="#2056C1"/>
                </svg>
              </div>
              <span>Run &amp; Status</span>
            </div>
          </Card.Header>
          <Card.Body>
            <Button
              className="sxi_primary_blue_btn btn-height"
              onClick={handleRun}
              disabled={!canRun || checkLoading || elementsLoading}
            >
              {running
                ? <><Spinner size="sm" animation="border" className="me-2" />Running...</>
                : checkLoading
                ? <><Spinner size="sm" animation="border" className="me-2" />Checking...</>
                : elementsLoading
                ? <><Spinner size="sm" animation="border" className="me-2" />Fetching Elements...</>
                : 'Run Selected Modules'}
            </Button>

            {/* Step status display — grouped by page */}
            {jobSteps.length > 0 && (
              <div className="mt-4">
                {Object.entries(stepsByPage).map(([page, steps]) => (
                  <div key={page} className="mb-3">
                    <div className="fw-semibold small text-muted mb-1" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {page}
                    </div>
                    {steps.map(step => (
                      <div key={step.id} className="d-flex align-items-start gap-2 mb-1 ms-2">
                        <span style={{ color: STEP_COLORS[step.status] || '#888', fontWeight: 600, minWidth: '1.2rem', lineHeight: '1.5' }}>
                          {STEP_ICONS[step.status] || '○'}
                        </span>
                        <div>
                          <span style={{ color: STEP_COLORS[step.status] || '#888', fontWeight: step.status === 'running' ? 600 : 400, fontSize: 13 }}>
                            {MODULES.find(m => m.id === step.module)?.label || step.label}
                          </span>
                          {step.status === 'running' && (
                            <Spinner size="sm" animation="border" className="ms-2" style={{ width: '0.75rem', height: '0.75rem' }} />
                          )}
                          {step.error && <div className="text-danger small mt-1">{step.error}</div>}
                          {step.module === 'ai_campaign' && step.status === 'completed' && step.result?.lpPreview && (() => {
                            const url     = step.result.lpPreview;
                            const baseUrl = (() => { try { const u = new URL(url); return u.origin + u.pathname; } catch { return url; } })();
                            return (
                              <div className="mt-1 d-flex align-items-center"
                                style={{ border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden', maxWidth: 400 }}>
                                <div style={{ padding: '0 10px', color: '#6c757d', flexShrink: 0 }}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="2" y1="12" x2="22" y2="12"/>
                                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                                  </svg>
                                </div>
                                <a href={encodeURI(url)} target="_blank" rel="noopener noreferrer" title={url}
                                  style={{ flex: 1, padding: '6px 4px', fontSize: 12, color: '#495057',
                                    textDecoration: 'none', overflow: 'hidden', whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis', display: 'block' }}>
                                  {baseUrl}...
                                </a>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(url);
                                    setCopiedStepId(step.id);
                                    setTimeout(() => setCopiedStepId(null), 2000);
                                  }}
                                  style={{ padding: '6px 14px', color: '#000', border: 'none',
                                    background: 'transparent', fontWeight: 500, fontSize: 12,
                                    cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
                                  {copiedStepId === step.id ? 'Copied!' : 'Copy URL'}
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {lastRun && !running && (
              <div className="mt-3 text-muted small">
                Last run: {new Date(lastRun.completed_at).toLocaleString()} — {lastRun.status === 'succeeded' ? '✓ Success' : '✗ Failed'}
              </div>
            )}
          </Card.Body>
        </Card>

      </div>

      {/* ── Element Selection Modal (AI Campaign) ── */}
      <Modal show={showElementSelect} onHide={() => setShowElementSelect(false)} size="xl" scrollable>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>Select Elements to Customize</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {elementsLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2 text-muted small">Fetching page elements...</p>
            </div>
          ) : Object.keys(pageElements).length === 0 ? (
            <p className="text-muted small">No elements found for the selected pages.</p>
          ) : (
            Object.entries(pageElements).map(([url, data]) => {
              const sel = elementSelections[url] || { text_elements: [], images: [] };
              return (
                <div key={url} className="mb-4">
                  <div className="fw-semibold small mb-2 d-flex align-items-center gap-2" style={{ color: 'var(--cux-accent, #2056C1)', wordBreak: 'break-all' }}>
                    <span>{url}</span>
                    {data.from_cache && (
                      <Badge bg="secondary" style={{ fontSize: 10, fontWeight: 400, flexShrink: 0 }}>
                        cached{data.fetched_at ? ` · ${new Date(data.fetched_at).toLocaleDateString()}` : ''}
                      </Badge>
                    )}
                  </div>

                  {/* Text Elements */}
                  {data.text_elements.length > 0 && (
                    <div className="mb-3">
                      <div className="text-muted small mb-1 fw-semibold">Text Elements</div>
                      <Table bordered size="sm" style={{ fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: '#F9FAFB' }}>
                            <th style={{ width: 36, textAlign: 'center' }}>
                              <Form.Check
                                type="checkbox"
                                checked={sel.text_elements.length === data.text_elements.length}
                                onChange={e => {
                                  setElementSelections(prev => ({
                                    ...prev,
                                    [url]: {
                                      ...(prev[url] || {}),
                                      text_elements: e.target.checked ? [...data.text_elements] : [],
                                    },
                                  }));
                                }}
                              />
                            </th>
                            <th>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.text_elements.map((el, idx) => {
                            const checked = sel.text_elements.some(s => s.query_selector === el.query_selector);
                            const display = (el.value || '').length > 80 ? el.value.slice(0, 80) + '…' : (el.value || '—');
                            return (
                              <tr key={idx} style={{ cursor: 'pointer' }} onClick={() => toggleElementSelection(url, 'text_elements', el)}>
                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                  <Form.Check
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleElementSelection(url, 'text_elements', el)}
                                    onClick={e => e.stopPropagation()}
                                  />
                                </td>
                                <td style={{ verticalAlign: 'middle', wordBreak: 'break-word' }}>{display}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )}

                  {/* Image Elements */}
                  {data.images.length > 0 && (
                    <div>
                      <div className="text-muted small mb-1 fw-semibold">Images</div>
                      <Table bordered size="sm" style={{ fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: '#F9FAFB' }}>
                            <th style={{ width: 36, textAlign: 'center' }}>
                              <Form.Check
                                type="checkbox"
                                checked={sel.images.length === data.images.length}
                                onChange={e => {
                                  setElementSelections(prev => ({
                                    ...prev,
                                    [url]: {
                                      ...(prev[url] || {}),
                                      images: e.target.checked ? [...data.images] : [],
                                    },
                                  }));
                                }}
                              />
                            </th>
                            <th>Preview</th>
                            <th style={{ width: 80 }}>Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.images.map((el, idx) => {
                            const checked = sel.images.some(s => s.query_selector === el.query_selector);
                            return (
                              <tr key={idx} style={{ cursor: 'pointer' }} onClick={() => toggleElementSelection(url, 'images', el)}>
                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                  <Form.Check
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleElementSelection(url, 'images', el)}
                                    onClick={e => e.stopPropagation()}
                                  />
                                </td>
                                <td style={{ verticalAlign: 'middle' }}>
                                  {el.url
                                    ? <img src={el.url} alt="" height={40} style={{ objectFit: 'contain', maxWidth: 120 }} />
                                    : <span className="text-muted">—</span>}
                                </td>
                                <td style={{ verticalAlign: 'middle' }}>
                                  <Badge bg={el.type === 'bg_img' ? 'secondary' : 'primary'} style={{ fontSize: 10 }}>
                                    {el.type}
                                  </Badge>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="sxi_primary_blue_btn"
            disabled={!anyElementSelected || running || elementsLoading}
            onClick={handleConfirmElements}
          >
            {running
              ? <><Spinner size="sm" animation="border" className="me-2" />Starting...</>
              : 'Start AI Campaign'}
          </Button>
          <Button
            variant="outline-secondary"
            disabled={elementsLoading}
            onClick={handleRefetchElements}
            title="Re-fetch elements from page (ignores cache)"
          >
            {elementsLoading
              ? <><Spinner size="sm" animation="border" className="me-2" />Fetching...</>
              : 'Re-fetch'}
          </Button>
          <Button variant="secondary" onClick={() => setShowElementSelect(false)} disabled={elementsLoading}>Cancel</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Conflict modal ── */}
      <Modal show={showConflict} onHide={() => setShowConflict(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>Conflicts Detected</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {conflicts.map((c, i) => {
            if (c.type === 'section_json') {
              return (
                <Alert key={i} variant="danger">
                  <Alert.Heading style={{ fontSize: 14 }}>Section JSON — Campaign Conflict</Alert.Heading>
                  <p className="small mb-2">
                    The following campaigns have Section JSON data for <strong>{c.page}</strong>.
                    Delete the campaign first, then re-run.
                  </p>
                  {c.campaigns.map(camp => (
                    <div key={camp.campaign_id} className="d-flex align-items-center gap-2 mb-1">
                      <span className="small">{camp.campaign_name || camp.campaign_id}</span>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={deleting === camp.campaign_id}
                        onClick={() => handleDeleteCampaign(camp.campaign_id)}
                      >
                        {deleting === camp.campaign_id
                          ? <Spinner size="sm" animation="border" />
                          : 'Delete Campaign'}
                      </Button>
                    </div>
                  ))}
                </Alert>
              );
            }
            return null;
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            disabled={checkLoading}
            onClick={async () => {
              setCheckLoading(true);
              try {
                const selectionsArr = buildSelectionsArr();
                const res = await axios.post(
                  `${app_url}/api/dev/re-run-modules/pre-check`,
                  { shop: shop_domain, selections: selectionsArr },
                  { withCredentials: true }
                );
                if (res.data.success) {
                  setConflicts(res.data.conflicts || []);
                  if ((res.data.conflicts || []).length === 0) {
                    setShowConflict(false);
                    await fetchAndShowElements(selectionsArr);
                  }
                }
              } catch (err) {
                toast.error('Pre-check failed');
              } finally {
                setCheckLoading(false);
              }
            }}
          >
            {checkLoading ? <Spinner size="sm" animation="border" /> : 'Retry Check'}
          </Button>
          <Button variant="secondary" onClick={() => setShowConflict(false)}>Cancel</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ReRunModules;
